import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Megaphone, Monitor, Sidebar,
  Image, Type, Clock, DollarSign, User, Mail, Loader2,
  Upload, X
} from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';
import PublicNav from '../components/PublicNav';
import Seo from '../components/Seo';
import { createAdCheckout, uploadAdImage } from '../services/api';

// ─── URL helpers ──────────────────────────────────────────────────────────────

function normalizeUrl(raw) {
  if (!raw || !raw.trim()) return raw;
  let url = raw.trim();
  // Fix broken protocol separators: https;// → https://
  url = url.replace(/^(https?)[;,]\/?\/?/i, '$1://');
  // Add https:// if no protocol present
  if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
  return url;
}

function isValidUrl(url) {
  if (!url) return false;
  try { new URL(url); return true; } catch { return false; }
}

function UrlInput({ value, onChange, placeholder = 'https://yourwebsite.com' }) {
  const [error, setError] = useState('');
  function handleBlur() {
    if (!value) return;
    const normalized = normalizeUrl(value);
    if (normalized !== value) onChange(normalized);
    setError(isValidUrl(normalized) ? '' : 'Please enter a valid URL (e.g. https://yoursite.com)');
  }
  const valid = value && isValidUrl(value);
  return (
    <div>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={e => { onChange(e.target.value); setError(''); }}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`w-full bg-dark-700 border rounded-lg px-3 py-2.5 pr-8 text-sm text-white placeholder-gray-600 focus:outline-none transition-colors ${
            error ? 'border-red-500/60 focus:border-red-500' : valid ? 'border-emerald-500/50 focus:border-emerald-500/70' : 'border-dark-600 focus:border-accent-primary/50'
          }`}
        />
        {valid && !error && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-400 text-xs">✓</span>
        )}
      </div>
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

const PRICES = {
  header:  { 24: 500,  48: 900,  72: 1200, 168: 2500 },
  sidebar: { 24: 300,  48: 550,  72: 750,  168: 1500 },
};

const DURATIONS = [
  { hours: 24,  label: '24 Hours' },
  { hours: 48,  label: '48 Hours' },
  { hours: 72,  label: '72 Hours' },
  { hours: 168, label: '7 Days'   },
];

const ACCENT_COLORS = ['amber', 'violet', 'emerald', 'blue', 'rose'];
const ACCENT_HEX = { amber:'#f59e0b', violet:'#8b5cf6', emerald:'#10b981', blue:'#3b82f6', rose:'#f43f5e' };

// ─── Mini preview ─────────────────────────────────────────────────────────────

function SidebarPreview({ adDetails }) {
  const accent = ACCENT_HEX[adDetails.accentColor] || ACCENT_HEX.amber;
  return (
    <div className="rounded-xl bg-dark-700 border p-4 max-w-[220px]"
      style={{ borderColor: `${accent}30` }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] font-bold text-gray-500 uppercase">{adDetails.tag || 'Sponsored'}</span>
        <span className="text-[9px] text-gray-600 font-mono">300×250</span>
      </div>
      {adDetails.imageUrl ? (
        <img src={adDetails.imageUrl} alt="preview" className="w-full rounded object-cover mb-2" style={{ minHeight: 80 }} />
      ) : (
        <>
          <p className="font-bold text-sm text-white mb-1 leading-snug">{adDetails.headline || 'Your Headline'}</p>
          <p className="text-gray-400 text-xs leading-relaxed mb-2">{adDetails.body || 'Your body text here'}</p>
        </>
      )}
      <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-lg"
        style={{ backgroundColor: `${accent}18`, color: accent, border: `1px solid ${accent}35` }}>
        {adDetails.cta || 'Learn More'} →
      </span>
    </div>
  );
}

function HeaderPreview({ adDetails }) {
  const accentColor = ACCENT_HEX[adDetails.accentColor] || '#8b5cf6';
  return (
    <div className="flex items-center gap-3 px-4 py-2 rounded-lg max-w-xl
                    bg-gradient-to-r from-violet-950/60 to-indigo-950/60
                    border border-violet-700/20">
      <span className="text-[9px] font-bold tracking-widest bg-violet-500/15 text-violet-400 px-1.5 py-0.5 rounded uppercase flex-shrink-0">Ad</span>
      {adDetails.imageUrl ? (
        <img src={adDetails.imageUrl} alt="preview" className="h-8 max-w-full object-contain" />
      ) : (
        <>
          <span className="text-sm truncate min-w-0">
            <span className="text-white font-semibold">{adDetails.headline || 'Your Headline'}</span>
            {adDetails.subText && <> — <span className="text-violet-300">{adDetails.subText}</span></>}
          </span>
          <span className="text-xs font-semibold flex-shrink-0" style={{ color: accentColor }}>
            {adDetails.cta || 'Learn More'} →
          </span>
        </>
      )}
    </div>
  );
}

// ─── Step 1: Choose slot + duration ──────────────────────────────────────────

function StepSlot({ slotType, setSlotType, duration, setDuration }) {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-white font-semibold mb-4">Choose Ad Format</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { id: 'header',  label: 'Header Banner',        size: '728×90 px',  icon: Monitor,  desc: 'Full-width leaderboard at the top of every page. Maximum visibility.' },
            { id: 'sidebar', label: 'Sidebar Rectangle',    size: '300×250 px', icon: Sidebar,  desc: 'Five premium sidebar slots on desktop screens. High engagement.' },
          ].map(opt => (
            <button key={opt.id} onClick={() => setSlotType(opt.id)}
              className={`p-5 rounded-2xl border text-left transition-all ${
                slotType === opt.id
                  ? 'bg-accent-primary/10 border-accent-primary/40'
                  : 'bg-dark-800 border-dark-700/50 hover:border-dark-600'
              }`}>
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  slotType === opt.id ? 'bg-accent-primary/20 text-accent-primary' : 'bg-dark-700 text-gray-400'
                }`}>
                  <opt.icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{opt.label}</p>
                  <p className="text-xs text-gray-500 font-mono mt-0.5">{opt.size}</p>
                </div>
                {slotType === opt.id && <Check className="w-4 h-4 text-accent-primary ml-auto" />}
              </div>
              <p className="text-xs text-gray-400">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-white font-semibold mb-4">Choose Duration</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {DURATIONS.map(d => {
            const price = PRICES[slotType || 'sidebar'][d.hours];
            const active = duration === d.hours;
            return (
              <button key={d.hours} onClick={() => setDuration(d.hours)}
                className={`p-4 rounded-xl border text-center transition-all ${
                  active
                    ? 'bg-accent-primary/10 border-accent-primary/40'
                    : 'bg-dark-800 border-dark-700/50 hover:border-dark-600'
                }`}>
                <Clock className={`w-4 h-4 mx-auto mb-1.5 ${active ? 'text-accent-primary' : 'text-gray-500'}`} />
                <p className={`font-semibold text-sm ${active ? 'text-accent-primary' : 'text-white'}`}>{d.label}</p>
                <p className="text-emerald-400 font-bold text-base mt-1">${price.toLocaleString()}</p>
                {active && <Check className="w-3.5 h-3.5 text-accent-primary mx-auto mt-1.5" />}
              </button>
            );
          })}
        </div>
      </div>

      {slotType && duration && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 flex items-center gap-3">
          <DollarSign className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <div>
            <p className="text-white font-semibold">
              {slotType === 'header' ? 'Header Banner' : 'Sidebar Rectangle'} —{' '}
              {DURATIONS.find(d => d.hours === duration)?.label}
            </p>
            <p className="text-emerald-400 font-bold text-lg">${PRICES[slotType][duration].toLocaleString()} total</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 2: Ad creative ──────────────────────────────────────────────────────

function StepCreative({ slotType, adDetails, setAdDetails }) {
  const [adType, setAdType] = useState(adDetails.imageUrl ? 'image' : 'text');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  function set(field, val) {
    setAdDetails(prev => ({ ...prev, [field]: val }));
  }

  async function handleFile(file) {
    if (!file) return;
    setUploadError('');
    setUploading(true);
    try {
      const url = await uploadAdImage(file);
      set('imageUrl', url);
    } catch (e) {
      setUploadError(e.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Image vs Text toggle */}
      <div>
        <h3 className="text-white font-semibold mb-3">Creative Type</h3>
        <div className="flex gap-2">
          {[
            { id: 'text',  label: 'Text Card', icon: Type  },
            { id: 'image', label: 'Image/Banner', icon: Image },
          ].map(t => (
            <button key={t.id} onClick={() => { setAdType(t.id); if (t.id === 'text') set('imageUrl', ''); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                adType === t.id
                  ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-primary'
                  : 'bg-dark-800 border-dark-700/50 text-gray-400 hover:text-white'
              }`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>
      </div>

      {adType === 'image' ? (
        <div className="space-y-4">
          {/* File upload zone */}
          <div>
            <label className="block text-xs text-gray-400 mb-2">
              Banner Image <span className="text-gray-600">({slotType === 'header' ? '728×90' : '300×250'} px · PNG, JPG, GIF, WebP · max 3MB)</span>
            </label>

            {adDetails.imageUrl ? (
              <div className="relative rounded-xl overflow-hidden border border-dark-600 bg-dark-700">
                <img src={adDetails.imageUrl} alt="preview" className="w-full max-h-40 object-contain" />
                <button
                  onClick={() => set('imageUrl', '')}
                  className="absolute top-2 right-2 w-7 h-7 bg-dark-900/80 rounded-full flex items-center justify-center hover:bg-red-500/80 transition-colors"
                >
                  <X className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            ) : (
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                className={`relative rounded-xl border-2 border-dashed transition-all ${
                  dragOver ? 'border-accent-primary/60 bg-accent-primary/5' : 'border-dark-600 bg-dark-700/50 hover:border-dark-500'
                }`}
              >
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.webp"
                  onChange={e => handleFile(e.target.files[0])}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="py-8 flex flex-col items-center gap-2 pointer-events-none">
                  {uploading ? (
                    <><Loader2 className="w-7 h-7 text-accent-primary animate-spin" /><p className="text-sm text-gray-400">Uploading…</p></>
                  ) : (
                    <><Upload className="w-7 h-7 text-gray-500" />
                    <p className="text-sm text-white font-medium">Click to upload or drag & drop</p>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF, WebP · max 3MB</p></>
                  )}
                </div>
              </div>
            )}
            {uploadError && <p className="text-xs text-red-400 mt-1">{uploadError}</p>}
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Destination URL *</label>
            <UrlInput value={adDetails.linkUrl} onChange={v => set('linkUrl', v)} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Alt / Headline (for accessibility)</label>
            <input type="text" value={adDetails.headline} onChange={e => set('headline', e.target.value)}
              placeholder="Your brand headline"
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary/50" />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Headline *</label>
              <input type="text" value={adDetails.headline} onChange={e => set('headline', e.target.value)}
                placeholder="Earn 18% APY"
                className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary/50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">CTA Button Text</label>
              <input type="text" value={adDetails.cta} onChange={e => set('cta', e.target.value)}
                placeholder="Start Now"
                className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary/50" />
            </div>
          </div>
          {slotType === 'header' ? (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Sub-text</label>
              <input type="text" value={adDetails.subText || ''} onChange={e => set('subText', e.target.value)}
                placeholder="Reach thousands of DeFi traders"
                className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary/50" />
            </div>
          ) : (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Body Text</label>
              <textarea value={adDetails.body || ''} onChange={e => set('body', e.target.value)} rows={2}
                placeholder="Audited smart contracts. Daily rewards. No lock-up."
                className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary/50 resize-none" />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Destination URL *</label>
              <UrlInput value={adDetails.linkUrl} onChange={v => set('linkUrl', v)} />
            </div>
            {slotType === 'sidebar' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Tag</label>
                <input type="text" value={adDetails.tag || ''} onChange={e => set('tag', e.target.value)}
                  placeholder="Sponsored"
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary/50" />
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-2">Accent Color</label>
            <div className="flex gap-2">
              {ACCENT_COLORS.map(col => (
                <button key={col} onClick={() => set('accentColor', col)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    adDetails.accentColor === col ? 'border-white scale-125' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: ACCENT_HEX[col] }}
                  title={col} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Live preview */}
      <div>
        <p className="text-xs text-gray-500 mb-3">Live Preview</p>
        {slotType === 'header'
          ? <HeaderPreview adDetails={adDetails} />
          : <SidebarPreview adDetails={adDetails} />
        }
      </div>
    </div>
  );
}

// ─── Step 3: Contact ──────────────────────────────────────────────────────────

function StepContact({ advertiserName, setAdvertiserName, advertiserEmail, setAdvertiserEmail }) {
  return (
    <div className="space-y-5 max-w-md">
      <div>
        <label className="block text-xs text-gray-400 mb-1">Your Name / Company *</label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input type="text" value={advertiserName} onChange={e => setAdvertiserName(e.target.value)}
            placeholder="Acme DeFi Protocol"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary/50" />
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1">Email Address *</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input type="email" value={advertiserEmail} onChange={e => setAdvertiserEmail(e.target.value)}
            placeholder="ads@yourcompany.com"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary/50" />
        </div>
        <p className="text-xs text-gray-600 mt-1.5">Stripe receipt and ad status updates will be sent here.</p>
      </div>
      <div className="bg-dark-800 border border-dark-700/50 rounded-xl p-4 space-y-2 text-xs text-gray-400">
        <p className="font-medium text-gray-300">What happens next?</p>
        <p>1. You pay via Stripe (secure, card or wallet)</p>
        <p>2. Our team reviews your ad within a few hours</p>
        <p>3. Once approved, your ad goes live and the timer starts</p>
        <p>4. Ad automatically turns off after your selected duration — no action needed</p>
      </div>
    </div>
  );
}

// ─── Step 4: Review ───────────────────────────────────────────────────────────

function StepReview({ slotType, duration, adDetails, advertiserName, advertiserEmail, onPay, paying, error }) {
  const price = PRICES[slotType]?.[duration] || 0;
  const durationLabel = duration === 168 ? '7 days' : `${duration} hours`;

  return (
    <div className="space-y-5 max-w-lg">
      <div className="bg-dark-800 border border-dark-700/50 rounded-2xl p-5 space-y-3">
        <h3 className="font-semibold text-white">Order Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Ad Format</span>
            <span className="text-white">{slotType === 'header' ? 'Header Banner 728×90' : 'Sidebar Rectangle 300×250'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Duration</span>
            <span className="text-white">{durationLabel}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Advertiser</span>
            <span className="text-white">{advertiserName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Email</span>
            <span className="text-white">{advertiserEmail}</span>
          </div>
          <div className="flex justify-between border-t border-dark-700 pt-2">
            <span className="text-gray-400">Headline</span>
            <span className="text-white">{adDetails.headline || '—'}</span>
          </div>
        </div>
      </div>

      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 flex items-center justify-between">
        <span className="text-gray-300 font-medium">Total</span>
        <span className="text-emerald-400 font-bold text-2xl">${price.toLocaleString()}</span>
      </div>

      <p className="text-xs text-gray-500">
        Payment processed securely by Stripe. Your ad goes live after team review.
        Ad runs automatically for {durationLabel} from approval — no manual cancellation needed.
      </p>

      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}

      <button onClick={onPay} disabled={paying}
        className="flex items-center justify-center gap-2 w-full py-3.5 bg-accent-primary text-dark-900 rounded-xl font-bold text-sm hover:bg-accent-primary/90 transition-colors disabled:opacity-50">
        {paying ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…</> : <>Pay ${price.toLocaleString()} → Continue to Payment</>}
      </button>
    </div>
  );
}

// ─── Main AdvertisePage ───────────────────────────────────────────────────────

const STEPS = ['Slot & Duration', 'Ad Creative', 'Contact', 'Review & Pay'];

export default function AdvertisePage() {
  const [step, setStep] = useState(0);
  const [slotType, setSlotType] = useState('sidebar');
  const [duration, setDuration] = useState(24);
  const [adDetails, setAdDetails] = useState({
    imageUrl: '', linkUrl: '', headline: '', subText: '', body: '',
    cta: 'Learn More', tag: 'Sponsored', accentColor: 'amber',
  });
  const [advertiserName, setAdvertiserName] = useState('');
  const [advertiserEmail, setAdvertiserEmail] = useState('');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');

  function canProceed() {
    if (step === 0) return slotType && duration;
    if (step === 1) return adDetails.linkUrl && (adDetails.headline || adDetails.imageUrl);
    if (step === 2) return advertiserName.trim() && advertiserEmail.includes('@');
    return true;
  }

  async function handlePay() {
    setPaying(true);
    setPayError('');
    try {
      const { checkoutUrl } = await createAdCheckout({
        slotType, duration, adDetails,
        advertiserName, advertiserEmail,
      });
      window.location.href = checkoutUrl;
    } catch (e) {
      setPayError(e.message || 'Something went wrong. Please try again.');
      setPaying(false);
    }
  }

  return (
    <div className="min-h-screen bg-dark-900">
      <Seo
        title="Advertise to active DeFi & crypto traders — from $300/day"
        description="Reach engaged DeFi and crypto trading users with sidebar, banner, and sponsored placements across the haythix platform. Self-serve checkout, fast approval."
        path="/advertise"
      />
      <PublicNav />

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-white mb-2">Reach DeFi Traders at Scale</h1>
          <p className="text-gray-400">Place your ad in front of thousands of active crypto traders — daily.</p>
          <div className="flex items-center justify-center gap-6 mt-5 text-sm">
            {[
              { label: 'Header Banner', size: '728×90 px', from: '$500' },
              { label: 'Sidebar Rectangle', size: '300×250 px', from: '$300' },
            ].map(s => (
              <div key={s.label} className="bg-dark-800 border border-dark-700/50 rounded-xl px-4 py-3 text-center">
                <p className="text-white font-semibold">{s.label}</p>
                <p className="text-gray-500 text-xs font-mono">{s.size}</p>
                <p className="text-accent-primary font-bold mt-1">from {s.from}/day</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((label, i) => (
            <div key={i} className="flex items-center gap-2 flex-1 min-w-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                i < step ? 'bg-accent-primary text-dark-900' :
                i === step ? 'bg-accent-primary/20 border border-accent-primary text-accent-primary' :
                'bg-dark-700 text-gray-500'
              }`}>
                {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-xs hidden sm:block truncate ${i === step ? 'text-white font-medium' : 'text-gray-500'}`}>{label}</span>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-1 ${i < step ? 'bg-accent-primary' : 'bg-dark-700'}`} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="bg-dark-800 border border-dark-700/50 rounded-2xl p-6 mb-6">
          {step === 0 && <StepSlot slotType={slotType} setSlotType={setSlotType} duration={duration} setDuration={setDuration} />}
          {step === 1 && <StepCreative slotType={slotType} adDetails={adDetails} setAdDetails={setAdDetails} />}
          {step === 2 && <StepContact advertiserName={advertiserName} setAdvertiserName={setAdvertiserName} advertiserEmail={advertiserEmail} setAdvertiserEmail={setAdvertiserEmail} />}
          {step === 3 && <StepReview slotType={slotType} duration={duration} adDetails={adDetails} advertiserName={advertiserName} advertiserEmail={advertiserEmail} onPay={handlePay} paying={paying} error={payError} />}
        </div>

        {/* Nav buttons */}
        {step < 3 && (
          <div className="flex justify-between">
            <button onClick={() => setStep(s => s - 1)} disabled={step === 0}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-dark-700 border border-dark-600 text-gray-300 text-sm font-medium hover:text-white transition-colors disabled:opacity-30">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={() => setStep(s => s + 1)} disabled={!canProceed()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-accent-primary text-dark-900 text-sm font-bold hover:bg-accent-primary/90 transition-colors disabled:opacity-40">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Contact footer */}
        <p className="text-center text-xs text-gray-600 mt-8">
          Ad enquiries: <a href="mailto:info@haythix.com" className="text-accent-primary hover:underline">info@haythix.com</a>
          <span className="mx-2">·</span>
          General contact: <a href="mailto:info@haythix.com" className="text-accent-primary hover:underline">info@haythix.com</a>
        </p>
      </div>
    </div>
  );
}
